/**
 * Consequence Type Configuration Service
 * Handles all consequence type operations (detention, suspension, etc.)
 * Wraps consequenceTypesService with high-level API
 */

import consequenceTypesService from '../../consequenceTypesService.js';

/**
 * Service for managing consequence type configurations
 */
class ConsequenceConfigService {
  constructor() {
    this.service = consequenceTypesService;
  }

  async getAll(schoolId) {
    return this.service.getAll(schoolId);
  }

  /**
   * Get consequence types formatted for select components
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<Array>} Array of select options
   */
  async getOptions(schoolId) {
    const types = await this.getAll(schoolId);
    return types.map(type => ({
      value: type.value,
      label: type.label,
      singular: type.singular,
      plural: type.plural,
      icon: type.icon,
      description: type.description
    }));
  }

  /**
   * Get all consequence types including inactive
   * @returns {Promise<Array>} Array of all consequence type objects
   */
  async getAllIncluding() {
    try {
      return await this.service.getAllIncludingInactive();
    } catch (error) {
      console.error('Error loading all consequence types:', error);
      throw error;
    }
  }

  /**
   * Get consequence type by value
   * @param {string} value - The consequence type value (e.g., 'detention')
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<Object|null>} Consequence type object or null
   */
  async getById(value, schoolId) {
    try {
      const types = await this.getAll(schoolId);

      // Try exact match first
      let type = types.find(t => t.value === value);
      if (type) return type;

      // Try case-insensitive match with underscores converted to hyphens
      const normalizedValue = value.toLowerCase().replace('_', '-');
      type = types.find(t => t.value === normalizedValue);
      if (type) return type;

      return null;
    } catch (error) {
      console.error('Error getting consequence type:', error);
      return null;
    }
  }

  /**
   * Get consequence types filtered by severity level
   * @param {string} severity - Severity level (low, medium, high, critical)
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<Array>} Filtered consequence types
   */
  async getBySeverity(severity, schoolId) {
    const types = await this.getAll(schoolId);
    return types.filter(type => type.severity === severity);
  }

  // Duplicate getBySeverity removed - correct version with schoolId above

  /**
   * Get consequence types that require action
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<Array>} Actionable consequence types
   */
  async getActionableTypes(schoolId) {
    const types = await this.getAll(schoolId);
    return types.filter(type => type.requiresAction);
  }

  /**
   * Validate if a consequence type value is valid
   * @param {string} type - Consequence type value
   * @returns {Promise<boolean>} True if valid
   */
  async isValid(type) {
    const result = await this.getById(type);
    return !!result;
  }

  /**
   * Get color for consequence type (for UI styling)
   * @param {string} type - Consequence type value
   * @returns {Promise<string>} Color value (e.g., 'warning', 'error')
   */
  async getColor(type) {
    const consequence = await this.getById(type);
    return consequence ? consequence.color : 'neutral';
  }

  /**
   * Get icon for consequence type
   * @param {string} type - Consequence type value
   * @returns {Promise<string>} Icon emoji
   */
  async getIcon(type) {
    const consequence = await this.getById(type);
    return consequence ? consequence.icon : '⚠️';
  }

  /**
   * Get default consequence type for a given severity level
   * @param {string} severity - Severity level
   * @returns {string} Default consequence type value
   */
  getDefaultForSeverity(severity) {
    const severityMap = {
      low: 'demerit',
      medium: 'detention',
      high: 'suspension',
      critical: 'suspension'
    };
    return severityMap[severity] || 'demerit';
  }

  /**
   * Clear cache (useful for testing or after admin changes)
   */
  clearCache() {
    this.service.clearCache();
  }
}

// Export singleton instance
export const consequenceConfig = new ConsequenceConfigService();

// Default export
export default consequenceConfig;
